// MA19 합배송스케쥴 — 서버 행 ↔ 표 행 변환과 저장 전 검증 (TM-4166).
//  검증 규칙은 moms-be ManagePairOrderScheduleService.normalize 와 같다. 화면이 먼저 막아 확인창을 닫지 않고 고칠 수 있게 하고,
//  서버는 같은 규칙으로 한 번 더 막는다.
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { PAIR_ORDER_SCHEDULE_MAX_SAVE_ROWS } from './consts'
import type { PairOrderScheduleDto, PairOrderScheduleRow, PairOrderScheduleSaveReq, PairOrderScheduleValues } from './types'

dayjs.extend(customParseFormat)

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '')

export const toRow = (dto: PairOrderScheduleDto): PairOrderScheduleRow => ({
  id: dto.id,
  SPR_NM: '',
  CLS_DATE: text(dto.clsDate),
  CUT_SEQ: text(dto.cutSeq),
  CUT_OFF_TIME: text(dto.cutOffTime),
  IS_CURRENT: text(dto.isCurrent),
  FST_GI_END: text(dto.fstGiEnd),
  LST_GI_END: text(dto.lstGiEnd),
  INS_DATE: text(dto.insDate),
  INS_BY: text(dto.insBy),
  UPD_DATE: text(dto.updDate),
  UPD_BY: text(dto.updBy),
})

export const isNewRow = (row: Record<string, unknown>): boolean => row.__new === true || (typeof row.id === 'string' && row.id.startsWith('new-'))

const isYmd = (v: string): boolean => /^\d{8}$/.test(v) && dayjs(v, 'YYYYMMDD', true).isValid()

/**
 * 한 행의 편집 값을 저장 값으로 바꾼다. 형식이 틀리면 사람이 읽을 문구로 던진다.
 * [stored] 가 있으면(수정 행의 서버 값) 그 값과 같은 칸은 검증하지 않는다 — 1.0 이 검증 없이 넣은 과거 값(14자리 출고예정일, 없는 날짜 등)을
 * 그대로 두고 다른 칸을 고칠 수 있게. 서버(ManagePairOrderScheduleService.normalize)도 같은 규칙이다.
 */
export const toValues = (row: Record<string, unknown>, stored?: Record<string, unknown>): PairOrderScheduleValues => {
  const clsDate = text(row.CLS_DATE)
  const cutSeqText = text(row.CUT_SEQ)
  const cutOffTime = text(row.CUT_OFF_TIME)
  const isCurrent = text(row.IS_CURRENT).toUpperCase()
  const fstGiEnd = text(row.FST_GI_END)
  const lstGiEnd = text(row.LST_GI_END)
  const at = clsDate ? `결제일 ${clsDate}` : '결제일이 빈 행'
  const changed = (value: string, key: string): boolean => !stored || value !== text(stored[key])

  if (!clsDate) throw new Error('결제일은 필수입니다.')
  // 출고예정일은 글로벌 컷오프 준비 배치가 결제일별로 읽는다. 이미 비어 있던 과거 행만 빈 채로 다른 칸을 고칠 수 있다(서버와 같음).
  if (!fstGiEnd && (!stored || text(stored.FST_GI_END))) throw new Error(`${at}: 출고예정일은 필수입니다.`)
  if (changed(clsDate, 'CLS_DATE') && !isYmd(clsDate)) throw new Error(`결제일은 yyyyMMdd 형식의 날짜입니다. (입력값: ${clsDate})`)
  if (fstGiEnd && changed(fstGiEnd, 'FST_GI_END') && !isYmd(fstGiEnd))
    throw new Error(`${at}: 출고예정일은 yyyyMMdd 형식의 날짜입니다. (입력값: ${fstGiEnd})`)
  if (lstGiEnd && changed(lstGiEnd, 'LST_GI_END') && !isYmd(lstGiEnd))
    throw new Error(`${at}: 최종출고일은 yyyyMMdd 형식의 날짜입니다. (입력값: ${lstGiEnd})`)
  if (cutOffTime && changed(cutOffTime, 'CUT_OFF_TIME') && !/^([01]\d|2[0-3])$/.test(cutOffTime))
    throw new Error(`${at}: 마감시각은 00~23 두 자리입니다. (입력값: ${cutOffTime})`)
  if (isCurrent && changed(isCurrent, 'IS_CURRENT') && isCurrent !== 'Y' && isCurrent !== 'N')
    throw new Error(`${at}: 잔여출고확정은 Y 또는 N 입니다. (입력값: ${isCurrent})`)
  if (cutSeqText && changed(cutSeqText, 'CUT_SEQ') && !/^\d+$/.test(cutSeqText))
    throw new Error(`${at}: 차수는 0 이상의 정수입니다. (입력값: ${cutSeqText})`)

  return {
    clsDate,
    cutSeq: cutSeqText ? Number(cutSeqText) : null,
    cutOffTime: cutOffTime || null,
    isCurrent: isCurrent || null,
    fstGiEnd: fstGiEnd || null,
    lstGiEnd: lstGiEnd || null,
  }
}

/** 조회한 서버 값을 검증 없이 저장 값 모양으로 — 수정 요청의 before(조회 당시 값)다. 형식 밖 과거 값도 그대로 보낸다. */
const toStoredValues = (stored: Record<string, unknown>): PairOrderScheduleValues => {
  const cutSeq = text(stored.CUT_SEQ)
  return {
    clsDate: text(stored.CLS_DATE),
    cutSeq: cutSeq ? Number(cutSeq) : null,
    cutOffTime: text(stored.CUT_OFF_TIME) || null,
    isCurrent: text(stored.IS_CURRENT) || null,
    fstGiEnd: text(stored.FST_GI_END) || null,
    lstGiEnd: text(stored.LST_GI_END) || null,
  }
}

/** 한 번에 보낼 수 있는 행 수(서버 MAX_SAVE_ROWS)를 넘으면 사람이 읽을 문구로 던진다. */
export const requireWithinSaveCap = (count: number): void => {
  if (count > PAIR_ORDER_SCHEDULE_MAX_SAVE_ROWS)
    throw new Error(`한 번에 ${PAIR_ORDER_SCHEDULE_MAX_SAVE_ROWS}건까지 저장할 수 있습니다. (선택 ${count}건) 나눠서 저장해 주세요.`)
}

/**
 * [저장] 대상 행(편집 반영본)을 신규·수정 요청으로 가른다. 요청 안 결제일 중복도 여기서 막는다.
 * [storedById] 는 수정 행의 서버 값(조회 결과)이다. 결제일을 그대로 둔 수정 행은 중복 검사에서 뺀다 — 과거 중복 행의 다른 칸 수정을 막지 않는다(서버와 같음).
 * 수정 행은 이 값을 before 로 함께 보낸다. 서버는 저장값이 before 와 다르면(조회 뒤 바뀜) 거절한다.
 */
export const toSaveRequest = (
  rows: Record<string, unknown>[],
  storedById: ReadonlyMap<string, Record<string, unknown>>,
): PairOrderScheduleSaveReq => {
  requireWithinSaveCap(rows.length)
  const creates: PairOrderScheduleSaveReq['creates'] = []
  const updates: PairOrderScheduleSaveReq['updates'] = []
  const checked: string[] = []
  const unchanged = new Set<string>()
  rows.forEach((row) => {
    const stored = isNewRow(row) ? undefined : storedById.get(String(row.id))
    if (!isNewRow(row) && !stored) throw new Error(`조회 결과에 없는 행입니다. 다시 조회해 주세요. (id=${String(row.id)})`)
    const values = toValues(row, stored)
    if (!stored) creates.push(values)
    else updates.push({ id: Number(row.id), ...values, before: toStoredValues(stored) })
    if (!stored || values.clsDate !== text(stored.CLS_DATE)) checked.push(values.clsDate)
    else unchanged.add(values.clsDate)
  })
  const seen = new Set<string>()
  // 신규·변경 결제일끼리, 그리고 결제일을 그대로 둔 수정 행과 겹치면 막는다(서버와 같음). 그대로 둔 행끼리는 과거 중복이라 두지 않는다.
  const dup = checked.filter((d) => unchanged.has(d) || (seen.has(d) ? true : (seen.add(d), false)))
  if (dup.length) throw new Error(`같은 결제일을 두 행에 저장할 수 없습니다. (${[...new Set(dup)].sort().join(', ')})`)
  return { creates, updates, deleteIds: [] }
}

/** 조회 조건(생성본 결제일 필드 값)을 서버 파라미터로 바꾼다. 비었거나 형식이 틀리면 null. */
export const toSearchReq = (range: unknown): { fromClsDate: string; toClsDate: string } | null => {
  if (!Array.isArray(range) || range.length < 2) return null
  const [from, to] = range.map((v) => dayjs(v as string | Date | dayjs.Dayjs))
  if (!from.isValid() || !to.isValid()) return null
  return { fromClsDate: from.format('YYYYMMDD'), toClsDate: to.format('YYYYMMDD') }
}

/** 서버 실패 응답의 사용자 문구(meta.message). 없으면 폴백. 응답 없는 요청 오류(시간 초과·네트워크)의 영문 문구는 보이지 않는다. */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const failed = error as { response?: { data?: { meta?: { message?: unknown } } }; isAxiosError?: boolean }
  const message = failed?.response?.data?.meta?.message
  if (typeof message === 'string' && message.trim()) return message
  if (error instanceof Error && error.message && !failed.response && !failed.isAxiosError) return error.message
  return fallback
}
